import { makeStyles } from '@fluentui/react-components';
import { Apps32Filled } from '@fluentui/react-icons';
import { NotificationToastDuration, NotificationType, useFabricContext } from '@idmc-ms-fabric/integration';
import { fabricServiceModels } from '@infa-hawk/services';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import ErrorScreen from '../../../component/core/error/ErrorScreen';
import PanelError from '../../../component/core/panel-error/PanelError';
import ProgressScreen from '../../../component/core/progress/ProgressScreen';
import { REGISTRATION_STATUS, TRegistrationDetails, TRegistrationDetailsMap } from '../../../context/business/Registration/Registration.types';
import { getRegistration, RegistrationStatusPolling } from '../../../context/business/Registration/RegistrationUtils';
import useDQTranslation from '../../../hooks/useDQTranslation';
import i18nKeys from '../../../i18n/i18nKeys';
import ScoreCardImage from '../../../images/scorecard.png';
import constants from '../../../utils/constants';
import { delay } from '../../../utils/coreUtils';
import { LocalStorage, SessionStorage } from '../../../utils/storage-utils';
import { useIDQALoggerContext } from '../IDQAlogger/IDQALoggerContext';

const useStyle = makeStyles({
	adminPanel: {
		height: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: '64px',
		justifyContent: 'center',
	},
	imgSize: {
		width: '674px',
		height: '374px',
		flexShrink: 0,
	},
});

const ACCEPTED_FABRIC_API_CONSENT_KEY = 'ACCEPTED_FABRIC_API_CONSENT';
const REGISTRATION_DETAILS_KEY = 'APP_REGISTRATION_DETAILS_KEY';
const GO_TO_SETUP_ID = 'GO_TO_SETUP_ID';
//TODO: These are temperaty constants, need to be removed with dyanamic values from backend
const REGISTRATION_URL_PROD = 'https://marketplace.informatica.com/registerFabric';
const REGISTRATION_URL_NON_PROD = 'https://marketplace-stage.informatica.com/registerFabric';
const PROD_HOST = 'dm1-us.informaticacloud.com';
type ContextType = fabricServiceModels.IGetRegistrationResponse;
const Context = createContext<ContextType | undefined>(undefined);

export const useAppRegistrationContext = (): ContextType => {
	const context = useContext(Context);
	if (!context) {
		throw `Application registration context is not yet available.`;
	}
	return context;
};

const AppRegistrationPollingInterval = 2 * 1000;
const AppRegistrationContext: React.FC<{ workspaceId: string }> = ({ workspaceId, children }) => {
	const { t } = useDQTranslation();
	const { extensionClient } = useFabricContext();
	const styles = useStyle();
	const [registrationUrl] = useState<string>(window.location.host.includes(PROD_HOST) ? REGISTRATION_URL_PROD : REGISTRATION_URL_NON_PROD);
	const [registrationDetails, _setRegistrationDetails] = useState<TRegistrationDetails>({ status: REGISTRATION_STATUS.LOADING });
	const registrationStatusPolling = useRef<RegistrationStatusPolling>();
	const { logger } = useIDQALoggerContext();
	const setRegistrationDetails = (workspaceId: string, details: TRegistrationDetails) => {
		_setRegistrationDetails(details);
		const registrations = SessionStorage.get<TRegistrationDetailsMap>(REGISTRATION_DETAILS_KEY) || {};
		SessionStorage.set<TRegistrationDetailsMap>(REGISTRATION_DETAILS_KEY, { ...registrations, [workspaceId]: details });
	};

	const getRegistrationDetails = (workspaceId: string) => {
		const details = SessionStorage.get<TRegistrationDetailsMap>(REGISTRATION_DETAILS_KEY);
		logger.info(details ? 'Fetched Org Regisitration details from local storage' : 'Details not present in Local');
		return details ? details[workspaceId] : undefined;
	};

	const getToken = async (attempt: number) => {
		const isfabricAPIConsentCompleted = LocalStorage.get<boolean>(ACCEPTED_FABRIC_API_CONSENT_KEY);
		try {
			const token = await extensionClient.auth.acquireAccessToken({
				additionalScopesToConsent: isfabricAPIConsentCompleted ? undefined : [constants.FABRIC_API_WAREHOUSE_SCOPE, constants.FABRIC_API_WORKSPACE_SCOPE],
			});
			if (!isfabricAPIConsentCompleted) {
				LocalStorage.set<boolean>(ACCEPTED_FABRIC_API_CONSENT_KEY, true);
			}
			logger.info('Acquired Auth token via Microsoft api');
			return token.token;
		} catch (error) {
			if (attempt < 11) {
				setRegistrationDetails(workspaceId, { status: REGISTRATION_STATUS.FAILURE });
				logger.info(`Acquiring Auth token via Microsoft api failed: Retry Count [${attempt}]`);
				await delay(5000);
				return await getToken(attempt + 1);
			} else {
				logger.error('Acquiring Auth token via Microsoft api failed');
				extensionClient.notification.open({
					title: `Registration Failed`,
					message: `Error while fetching azure access token`,
					notificationType: NotificationType.Error,
					duration: NotificationToastDuration.Medium,
				});
				setRegistrationDetails(workspaceId, { status: REGISTRATION_STATUS.FORCE_STOP });
				throw new Error('Error while fetching azure access token');
			}
		}
	};

	const init = async () => {
		const accessToken = await getToken(0);
		let registrationDetails = getRegistrationDetails(workspaceId);
		if (!registrationDetails || registrationDetails.status !== REGISTRATION_STATUS.COMPLETED) {
			registrationDetails = await getRegistration({ accessToken, workspaceId });
		}
		setRegistrationDetails(workspaceId, registrationDetails);
		if (registrationDetails.status !== REGISTRATION_STATUS.COMPLETED) {
			cleanUp();
			const polling = new RegistrationStatusPolling(workspaceId, accessToken, {
				interval: AppRegistrationPollingInterval,
				onChange(details) {
					if (details.status === REGISTRATION_STATUS.COMPLETED) {
						logger.info('Registration Details fetched successfully.');
						polling.stop();
					}
					if (registrationDetails.status !== details.status) {
						setRegistrationDetails(workspaceId, details);
					}
				},
			});
			registrationStatusPolling.current = polling;
			polling.start();
		}
	};

	const cleanUp = () => {
		if (registrationStatusPolling.current) {
			registrationStatusPolling.current.stop();
		}
	};

	useEffect(() => {
		init();
		return () => {
			cleanUp();
		};
	}, [workspaceId]);
	const goToSetup = React.useCallback(async (registrationUrl: string) => {
		try {
			const response = await extensionClient.navigation.openBrowserTab({ url: registrationUrl });
			if (response) {
				logger.info('Navigated to registration page');
			}
		} catch (err) {
			console.log(err);
		}
	}, []);
	return registrationDetails.status === REGISTRATION_STATUS.COMPLETED ? (
		<Context.Provider value={registrationDetails.details}>{children}</Context.Provider>
	) : registrationDetails.status === REGISTRATION_STATUS.LOADING ? (
		<ProgressScreen icon={<Apps32Filled />} message={t(i18nKeys.main.checkAppRegistration)} />
	) : registrationDetails.status === REGISTRATION_STATUS.INPROGRESS ? (
		<ProgressScreen icon={<Apps32Filled />} message={t(i18nKeys.main.appRegistrationInProgress)} />
	) : registrationDetails.status === REGISTRATION_STATUS.ORG_DOESNOT_EXIST ? (
		<div className={styles.adminPanel}>
			<PanelError
				title={t(i18nKeys.main.cantCreateDQItem)}
				errorBtns={[{ id: GO_TO_SETUP_ID, label: t(i18nKeys.main.goToSetup), btnProps: { appearance: 'primary', size: 'small' } }]}
				errorDetails={t(i18nKeys.main.registrationPageMessage)}
				errorBtnClb={() => goToSetup(registrationUrl)}
			/>
			<div>
				<img className={styles.imgSize} src={ScoreCardImage} alt="Profiling Results" />
			</div>
		</div>
	) : registrationDetails.status === REGISTRATION_STATUS.FAILURE ? (
		<ProgressScreen icon={<Apps32Filled />} message={t(i18nKeys.main.retryAppRegistrationCheck)} />
	) : registrationDetails.status === REGISTRATION_STATUS.FORCE_STOP ? (
		<ErrorScreen message={t(i18nKeys.main.mulitpleAttemptFailure)} />
	) : null;
};

export default AppRegistrationContext;
